import React, { Component } from "react";
import ApiCall from "../../utils/api_call";
import Breadcrumb from "./Breadcrumb";
import CookieValue from "../../utils/cookie_value";
import FetchResource from "../../utils/fetch_resource";
import File from "./File";
import Folder from "./Folder";
import EditModal from "./EditModal";
import ContextMenu from "./ContextMenu";
import Notice from "../Notice";
import Spinner from "../Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faFolder,
  faFile,
  faFileImage,
  faFileCsv,
  faFileAudio,
  faFilePdf,
  faFilePowerpoint,
  faFileWord,
} from "@fortawesome/free-solid-svg-icons";
import LogIf from "../../utils/log_if";

const TZ_OPTS = {
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZoneName: "short",
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
};

const FILE_TYPE = "static_file";
const FOLDER_TYPE = "folder";
const API_BASE = process.env.API_URL;
const CLIENT_BASE = process.env.BASE_URL;

export default class FoldersMainContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      breadcrumb: [{ id: null, name: "Home" }],
      modal_folder: null,
      modal_static_file: null,
      folders: [],
      files: [],
      sortBy: "",
      openFolderId: null,
      contextMenuItemName: null,
      contextMenuItemId: null,
      contextMenuItemType: null,
      contextMenuItemUrl: null,
      data_ready: false,
      notice_message: "",
      display_notice: false,
      display_error: false,
      permissions: {},
    };
    this.fileRef = React.createRef();
  }

  componentDidMount = () => {
    this._isMounted = true;
    this.openFolder();
  };

  componentWillUnmount = () => {
    this._isMounted = false;
  };

  componentDidUpdate = (prevProps, prevState, snapshot) => {
    if (this._isMounted) {
      if (prevState.openFolderId !== this.state.openFolderId) {
        this.openFolder();
      }
    }
  };

  displayError = (error) => {
    LogIf("debug", "should show error");
    let message;
    if (error instanceof Array) {
      message = error.join(", ");
    } else if (error instanceof String) {
      message = error;
    } else if (error instanceof Object) {
      message = Object.keys(error)
        .map((key) => {
          return `${key} ${error[key]}`;
        })
        .join(", ");
    }
    if (this._isMounted) {
      this.setState({
        data_ready: true,
        notice_message: message,
        display_error: true,
        display_notice: false,
      });
    }
  };

  displayNotice = (message) => {
    if (this._isMounted) {
      this.setState({
        data_ready: true,
        notice_message: message,
        display_error: false,
        display_notice: true,
      });
    }
  };

  openFolder = () => {
    const { openFolderId, sortBy } = this.state;
    const filters = `[parent_id]=${openFolderId}&order=${sortBy}`;
    /**get child folders of open folder**/
    FetchResource(
      API_BASE + `${FOLDER_TYPE}s`,
      `${FOLDER_TYPE}${filters}`,
      this
    )
      .then((res) => {
        if (this._isMounted) {
          this.setState({ data_ready: true });
        }
      })
      .catch((err) => {
        this.displayError(`Getting folders: ${err}`);
      });
    /**Get static files in open folder**/
    FetchResource(API_BASE + `${FILE_TYPE}s`, `${FILE_TYPE}${filters}`, this)
      .then(() => {})
      .catch((err) => {
        this.displayError(`Getting files: ${err}`);
      });
  };

  handleOpenFolder = (folderId) => {
    this.setState({
      data_ready: false,
      openFolderId: folderId,
      folders: [],
      files: [],
    });
  };

  handleSetContextMenu = (id, key, name, url, event) => {
    if (event != null) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.setState({
      contextMenuItemId: id,
      contextMenuItemType: key,
      contextMenuItemName: name,
      contextMenuItemUrl: url,
    });
  };

  /** Builds request body when attaching a form.  **/
  buildFormData = (modal_item, type) => {
    const formData = new FormData();
    formData.append(`${type}[name]`, modal_item["name"]);
    formData.append(`${type}[parent_id]`, modal_item["parent_id"]);
    formData.append(`${type}[file]`, modal_item["file"]);
    return formData;
  };

  /** Builds simple request body when no upload file is included **/
  buildFormJson = (modal_item, type) => {
    let data = {};
    data[type] = {
      name: modal_item["name"],
      parent_id: modal_item["parent_id"],
    };
    return JSON.stringify(data);
  };

  headersForFormType = (uploadingFile) => {
    return uploadingFile
      ? {
          /** with body from this.buildFormData **/
          Authorization: "Bearer " + CookieValue(),
        }
      : {
          /** with body from this.buildFormJson **/
          "Content-Type": "application/json",
          Authorization: "Bearer " + CookieValue(),
        };
  };

  /**
   * 1) Build request from state['modal_folder'] or state['modal_file'].
   * 2) Set state['data_ready'] to false and state[`modal_${itemType}`]
   *    to null.
   * 3) POST or UPDATE Folders or Files with request data.
   * 4) Updates in state upon receiving a response:
   *    i.   notice_message (success or failure message)
   *    ii.  display_notice (bool-- display notice as a success alert)
   *    iii. display error (bool-- display notice as an error alert)
   *    iv.  data_ready back to true
   **/
  handleUpdateFromModal = (itemType, event) => {
    event.preventDefault();
    /** STEP 1 **/
    const modal_item = this.state[`modal_${itemType}`];
    const url = API_BASE + `${itemType}s/${modal_item.id || ""}`;
    const isNew = modal_item.id == null;
    const uploadingFile = modal_item["file"] != null;
    const body = uploadingFile
      ? this.buildFormData(modal_item, itemType)
      : this.buildFormJson(modal_item, itemType);
    const reqOptions = {
      method: isNew ? "POST" : "PATCH",
      headers: this.headersForFormType(uploadingFile),
      body: body,
    };

    /** STEP 2 **/
    let unsetPropsObj = { data_ready: false };
    unsetPropsObj[`modal_${itemType}`] = null;
    if (this._isMounted) {
      this.setState(unsetPropsObj);
      /** STEP 3 **/
      ApiCall(url, reqOptions, this)
        .then((data) => {
          /** STEP 4 **/
          if (!data.errors && data.res_status != "unauthorized") {
            if (this._isMounted) {
              this.setState(data);
            }
            this.displayNotice(
              `Success: ${isNew ? "created" : "updated"} ${itemType}.`
            );
          } else {
            this.displayError(data.errors);
          }
        })
        .catch((e) => {
          this.displayError(
            `Error ${isNew ? "creating" : "updating"} ${itemType}: ${e}`
          );
        });
    }
  };

  /**
   * Set state['modal_folder'] or state['modal_file'] so that
   * the add or edit modal will become visible.
   * If editing an existing item, prepopulates the modal with
   * current data.
   **/
  setEditingItem = (itemType, itemId, clickedParentId) => {
    const parentId = clickedParentId || this.state.openFolderId;
    let itemData;
    if (itemId != null) {
      this.handleSetContextMenu();
      const url = API_BASE + `${itemType}s/${itemId}/edit`;
      FetchResource(url, "", this);
    } else {
      itemData = {};
      itemData[`modal_${itemType}`] = {
        id: null,
        name: "",
        parent_id: parentId,
      };
    }
    this.setState(itemData);
  };

  /**
   * Clears out either modal_folder or modal_static_file state objects.
   * When state updates, this hides the EditModal component.
   **/
  clearEditingItem = (itemType) => {
    let data = {};
    data[`modal_${itemType}`] = null;
    this.setState(data);
  };

  /**Handles changes to fields in EditModal.jsx**/
  handleModalChange = (fieldName, event) => {
    /** extract nested field names. e.g., abc[def] => ['abc', 'def'] **/
    const state = this.state;
    const fieldNameArr = fieldName.match(/\w+|\[(\w+)\1\]/g);
    let changedFieldObj = state;
    let changedFieldKey = fieldNameArr[fieldNameArr.length - 1];
    for (let fieldKey of fieldNameArr) {
      if (fieldKey != changedFieldKey) {
        changedFieldObj = changedFieldObj[fieldKey];
      }
    }
    if (changedFieldKey == "file") {
      changedFieldObj[changedFieldKey] = event.target.files[0];
    } else {
      changedFieldObj[changedFieldKey] = event.target.value;
    }
    this.setState(state);
  };

  confirmDeleteItem = (itemType, itemId) => {
    let itemName = this.state.contextMenuItemName;
    const confirmMessage = `Are you sure you want to delete ${itemName}?`;
    const addendum =
      itemType == "folder"
        ? " PLEASE NOTE: Any child folders and files will also be deleted."
        : "";
    this.handleSetContextMenu();
    if (confirm(`${confirmMessage}${addendum}`)) {
      this.handleDeleteItem(itemType, itemId);
    }
  };

  /**
   * Delete a file or a folder and it's children.
   * Triggered by this.confirmDeleteItem
   **/
  handleDeleteItem = (itemType, itemId) => {
    const { openFolderId } = this.state;
    const url = API_BASE + `${itemType}s/${itemId}`;
    let body = {};
    body[itemType] = { parent_id: openFolderId };
    const reqOptions = {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + CookieValue(),
      },
      body: JSON.stringify(body),
    };
    ApiCall(url, reqOptions, this)
      .then((data) => {
        if (
          !data.errors &&
          data.res_status != "unauthorized" &&
          data.status != 500
        ) {
          if (this._isMounted) {
            this.setState(data);
          }
          this.displayNotice(`Success: deleted ${itemType}.`);
        } else {
          this.displayError(data.errors);
        }
      })
      .catch((e) => {
        this.displayError(
          `Error deleting ${itemType}${e == null ? "" : `: ${e.toString()}`}`
        );
      });
  };

  generateAddBtns = (canManage) => {
    return canManage ? (
      <div>
        <button
          className="btn btn-primary"
          onClick={() => {
            this.setEditingItem(FOLDER_TYPE);
          }}
        >
          <FontAwesomeIcon icon={faPlus} />
          <span> Add Folder </span>
          <FontAwesomeIcon icon={faFolder} />
        </button>
        <button
          className="btn btn-primary ml-2"
          onClick={() => {
            this.setEditingItem(FILE_TYPE);
          }}
        >
          <FontAwesomeIcon icon={faPlus} />
          <span> Add File </span>
          <FontAwesomeIcon icon={faFile} />
        </button>
      </div>
    ) : (
      ""
    );
  };

  generateFolderListItems = (folders, canManage) => {
    return folders.map((folder) => {
      return (
        <Folder
          key={`folder-${folder.id}`}
          folder={folder}
          tzOpts={TZ_OPTS}
          handleOpenFolder={this.handleOpenFolder}
          handleOpenContextMenu={this.handleSetContextMenu}
          editMode={canManage}
        />
      );
    });
  };

  generateFileListItems = (files, canManage) => {
    return files.map((file) => {
      return (
        <File
          key={`file-${file.id}`}
          file={file}
          tzOpts={TZ_OPTS}
          handleOpenContextMenu={this.handleSetContextMenu}
          editMode={canManage}
        />
      );
    });
  };

  render() {
    const {
      breadcrumb,
      folders,
      files,
      modal_folder,
      modal_static_file,
      sortBy,
      openFolderId,
      contextMenuItemName,
      contextMenuItemId,
      contextMenuItemType,
      contextMenuItemUrl,
      data_ready,
      notice_message,
      display_notice,
      display_error,
      permissions,
    } = this.state;

    const noContent = folders.length + files.length == 0;
    const noneFound = data_ready ? (
      <li key="nonefound" className="list-group-item text-muted">
        None Found
      </li>
    ) : (
      <Spinner />
    );
    const addBtns = this.generateAddBtns(permissions["manage"]);
    const modal_item = modal_folder || modal_static_file || {};

    return (
      <div className="container" style={{ fontSize: "1rem" }}>
        <Notice
          display_error={display_error}
          display_notice={display_notice}
          notice_message={notice_message}
        />

        <div className="card inner-buffer alert-secondary">
          <div
            title="Navigation and add buttons"
            className="d-flex justify-content-between align-items-start"
          >
            <Breadcrumb
              breadcrumb={breadcrumb}
              handleOpenFolder={this.handleOpenFolder}
              currentFolder={openFolderId}
            />
            {addBtns}
          </div>
          <ul
            title="Main directory structure"
            className="list-group"
            style={{ height: "35vh", overflow: "auto" }}
          >
            {noContent ? noneFound : ""}
            {this.generateFolderListItems(folders, permissions["manage"])}
            {this.generateFileListItems(files, permissions["manage"])}
          </ul>
        </div>

        <EditModal
          item={modal_folder || modal_static_file}
          itemType={modal_folder != null ? FOLDER_TYPE : FILE_TYPE}
          fileRef={this.fileRef}
          handleInput={this.handleModalChange}
          handleClose={this.clearEditingItem}
          handleSubmit={this.handleUpdateFromModal}
          canManage={permissions["manage"]}
        />

        <ContextMenu
          itemName={contextMenuItemName}
          itemType={contextMenuItemType}
          itemId={contextMenuItemId}
          handleEdit={this.setEditingItem}
          handleDelete={this.confirmDeleteItem}
          handleCancel={this.handleSetContextMenu}
          canManage={permissions["manage"]}
          url={contextMenuItemUrl}
        />
      </div>
    );
  }
}
